import Customer from '@/app/_customers/Customer.vue';
import customerRoutes from '@/router/customer/routes-customer';
import Employee from '@/app/_employees/Employee.vue';
import employeeRoutes from '@/router/employee/routes-employee';
import LoginPage from '@/app/_users/pages/LoginPage.vue';
import partnerRoutes from '@/router/partner/routes-partner';
import Partner from '@/app/_partners/Partner.vue';
import ErrorNotFound from '@/app/_shared/pages/ErrorNotFound.vue';
import ErrorInternalServer from '@/app/_shared/pages/ErrorInternalServer.vue';
import NotificationsRedirect from '@/app/_shared/pages/NotificationsRedirect.vue';
import {
  ROUTE_COMMON,
  ROUTES_CUSTOMERS,
  ROUTES_EMPLOYEES,
  ROUTES_PARTNERS,
  ROUTES_SUPERVISORS,
  ROUTES_USERS,
} from '@/app/data/route_constants';
import { createRouter, createWebHistory } from 'vue-router';
import store from '@/store/index';
import Supervisor from '@/app/_supervisors/Supervisor.vue';
import supervisorRoutes from '@/router/supervisor/routes-supervisor';
import { getInstance } from '@/auth';
import User from '@/app/_users/User.vue';
import { PROFILE_TYPES, Tenant } from '@/app/data/model_constants';
import userRoutes from '@/router/user/routes-user';
import { VITE_ENV_REVIEW } from '@/app/data/general_constants';

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      name: ROUTES_EMPLOYEES.ROOT,
      path: '/employee/:subdomain',
      component: Employee,
      children: employeeRoutes,
      props: true,
    },
    {
      name: ROUTES_CUSTOMERS.ROOT,
      path: '/customer/:subdomain',
      component: Customer,
      children: customerRoutes,
      props: true,
    },
    {
      name: ROUTES_PARTNERS.ROOT,
      path: '/partner/:subdomain',
      component: Partner,
      children: partnerRoutes,
      props: true,
    },
    {
      name: ROUTES_SUPERVISORS.ROOT,
      path: '/supervisor/',
      component: Supervisor,
      children: supervisorRoutes,
    },
    {
      name: ROUTES_USERS.ROOT,
      path: '/user/',
      component: User,
      children: userRoutes,
    },
    {
      name: ROUTE_COMMON.ROOT,
      path: '',
    },
    {
      name: ROUTE_COMMON.NOTIFICATIONS_REDIRECT,
      path: '/notifications/:subdomain/:profile_type/:profile_id/:notification_id',
      component: NotificationsRedirect,
    },
    {
      name: ROUTE_COMMON.ERROR_NOT_FOUND,
      path: '/404',
      component: ErrorNotFound,
    },
    {
      name: ROUTE_COMMON.ERROR_INTERNAL_SERVER,
      path: '/500',
      component: ErrorInternalServer,
    },
    {
      name: ROUTE_COMMON.NOT_FOUND,
      path: '/:catchAll(.*)*',
      redirect: '/404',
    },
    {
      name: ROUTE_COMMON.LOGIN,
      path: '/login',
      component: LoginPage,
    },
  ],
});

// add authentication to each route
router.beforeEach((to, from, next) => {
  const auth = getInstance();
  if (auth.is_authenticated) return next();

  // session cookie presence determines authentication (actual cookie verification is done purely on backend decrypt)
  if (!!document.cookie.match(/(^|; )_session=/) || to.name === ROUTE_COMMON.LOGIN) {
    // kicks off App#authenticated watcher, which inits the user (and does a redirect)
    if (!window.location.pathname.startsWith('/login')) auth.is_authenticated = true;
    return next(); // irrelevant route navigation (due to redirect)
  }
  const redirect = to.fullPath === '/' ? null : encodeURIComponent(to.fullPath);
  const parts = window.location.hostname.split('.');
  const is_review = import.meta.env.VITE_ENV === VITE_ENV_REVIEW && !import.meta.env.IS_LOCAL;
  if ((!is_review && parts.length > 2) || (is_review && parts.length > 3)) {
    const subdomain = parts[0];
    parts.splice(0, 1);
    const hostname = parts.join('.');
    let href = `https://${hostname}/login?subdomain=${subdomain}`;
    if (redirect) href += `&redirect=${encodeURIComponent(to.fullPath)}`;
    window.location.href = href;
    return;
  }
  let href = `https://${window.location.hostname}/login`;
  if (redirect) href += `?redirect=${encodeURIComponent(to.fullPath)}`;
  window.location.href = href;
  return;
});

// supervisor impersonations or test clients: append profile meta params (facilitates URL sharing internally)
router.beforeEach((to, from, next) => {
  const current_profile_true_type = _.get(store, 'getters.current_profile_true.type');
  const is_test = _.get(store, 'getters.current_clientsi.attributes.status') === Tenant.STATUS_TEST;
  const is_impersonation = _.get(store, 'getters.current_is_impersonation');
  const is_impersonation_supervisor = is_impersonation && current_profile_true_type === PROFILE_TYPES.SUPERVISOR;
  const query_profile_id = to.query._profile_id;
  const current_profile_id = _.get(store, 'getters.current_profile.id');
  const current_profile_type = _.get(store, 'getters.current_profile.type');

  if (!query_profile_id && current_profile_id && current_profile_type && (is_impersonation_supervisor || is_test)) {
    to.query._profile_id = current_profile_id;
    to.query._profile_type = current_profile_type;

    if (to.path !== from.path) {
      return next({ path: to.path, query: to.query, hash: to.hash });
    }
  }
  return next();
});

// scroll to top after change route
router.beforeEach((to, from, next) => {
  const page = document.getElementById('page');
  if (page && from.path !== to.path) page.scrollTop = 0;
  next();
});

export default router;
